import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WORK_STATE_LABELS, type WorkState } from '@scc/shared';
import { decideProposal, fetchMe, fetchRoadmap, type Me } from '../api';
import { Badge, Empty, ErrorBox, Loading, ProgressBar, Section, Table } from '../components/ui';
import { Kanban, StateSummary } from '../components/kanban';
import { ItemPanel, ItemTable, PanelLateral } from './TasksPage';
import { TaskEditor } from './TaskEditor';
import type { WorkItem } from '../api';

/**
 * H6.8 · Pestañas de roadmap y trabajo de un proyecto.
 *
 * Las propuestas del importador se muestran arriba y separadas: **no cuentan
 * para ningún indicador** hasta que alguien las acepta, y la pantalla lo dice.
 */
export function RoadmapTab({ slug, me }: { slug: string; me: Me }): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['roadmap', slug], queryFn: () => fetchRoadmap(slug) });
  const [showAll, setShowAll] = useState(false);

  const decide = useMutation({
    mutationFn: (input: { id: number; decision: 'accept' | 'reject' }) =>
      decideProposal(input.id, input.decision),
    onSuccess: () => client.invalidateQueries(),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { tracks, milestones, proposals } = query.data;
  const isAdmin = me.role === 'admin';
  const shown = showAll ? proposals : proposals.slice(0, 25);

  return (
    <>
      {proposals.length > 0 && (
        <Section
          title={`${proposals.length} propuesta(s) leídas del Markdown`}
          subtitle="Sin aceptar no cuentan para ningún indicador. Cada una dice de qué fichero y línea sale."
          action={
            proposals.length > 25 ? (
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="text-[12px] underline opacity-70 hover:opacity-100"
              >
                {showAll ? 'ver menos' : `ver las ${proposals.length}`}
              </button>
            ) : undefined
          }
        >
          <Table head={['Tipo', 'Código', 'Nombre', 'Estado propuesto', 'Origen', '']}>
            {shown.map((proposal) => (
              <tr
                key={proposal.id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 text-[11px]">
                  {proposal.kind === 'track' ? 'track' : 'tarea'}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{proposal.code ?? '—'}</td>
                <td className="px-3 py-2 text-[12px]">{proposal.name}</td>
                <td className="px-3 py-2 text-[11px]">
                  {proposal.proposedState ? (
                    WORK_STATE_LABELS[proposal.proposedState as WorkState]
                  ) : (
                    <span
                      className="text-[var(--color-ink-muted)]"
                      title="El Markdown no dice en qué estado está"
                    >
                      sin determinar
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-[var(--color-ink-muted)]">
                  {proposal.sourceRef}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {isAdmin && (
                    <span className="flex gap-1.5">
                      <button
                        type="button"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: proposal.id, decision: 'accept' })}
                        className="text-[11px] underline opacity-70 hover:opacity-100 disabled:opacity-40"
                      >
                        aceptar
                      </button>
                      <button
                        type="button"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: proposal.id, decision: 'reject' })}
                        className="text-[11px] text-[var(--color-ink-muted)] underline hover:text-[var(--color-ink)] disabled:opacity-40"
                      >
                        descartar
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
          {decide.isError && (
            <div className="mt-2">
              <ErrorBox error={decide.error} />
            </div>
          )}
        </Section>
      )}

      <Section title="Hitos">
        {milestones.length === 0 ? (
          <Empty>
            Ningún hito registrado. Sin hitos, el dashboard no puede mostrar «próximo hito».
          </Empty>
        ) : (
          <Table head={['Hito', 'Fecha', 'Estado']}>
            {milestones.map((milestone) => (
              <tr
                key={milestone.id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2">{milestone.name}</td>
                <td className="px-3 py-2 text-[12px]">{milestone.dueDate ?? 'sin fecha'}</td>
                <td className="px-3 py-2">
                  {milestone.completedAt ? (
                    <Badge tone="good">completado</Badge>
                  ) : (
                    <Badge>pendiente</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Tracks" subtitle={`${tracks.length} registrados`}>
        {tracks.length === 0 ? (
          <Empty>
            Ningún track todavía. Acepta alguna propuesta de arriba o créalos desde la API.
          </Empty>
        ) : (
          <Table
            head={['Código', 'Track', 'Estado', 'Pri.', 'Progreso', 'Items', 'Abiertos', 'Origen']}
          >
            {tracks.map((track) => (
              <tr
                key={track.id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2 font-mono text-[11px]">{track.code ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className="font-medium">{track.name}</span>
                  {track.sourceRef && (
                    <div
                      className="font-mono text-[10px] text-[var(--color-ink-muted)]"
                      title="De dónde salió este track"
                    >
                      {track.sourceRef}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[12px]">
                  {WORK_STATE_LABELS[track.state as WorkState] ?? track.state}
                </td>
                <td className="px-3 py-2 text-[12px]">{track.priority}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  <ProgressBar pct={track.progressPct} />
                </td>
                <td className="px-3 py-2 tabular-nums">{track.itemCount}</td>
                <td className="px-3 py-2 tabular-nums">{track.openCount}</td>
                <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
                  {track.origin === 'derived' ? 'importado' : 'portal'}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

/** Pestaña de trabajo del proyecto: el mismo tablero, filtrado. */
export function WorkTab({ slug }: { slug: string }): JSX.Element {
  const [view, setView] = useState<'kanban' | 'table'>('kanban');
  const [selected, setSelected] = useState<WorkItem | null>(null);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<WorkItem | null>(null);
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const query = useQuery({ queryKey: ['roadmap', slug], queryFn: () => fetchRoadmap(slug) });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const items = query.data.items;

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <StateSummary items={items} />
        {me.data && (me.data.role === 'admin' || me.data.role === 'member') && (
          <button
            type="button"
            onClick={() => setCreando(true)}
            className="shrink-0 rounded bg-[var(--color-ink)] px-3 py-1.5 text-[13px] text-[var(--color-surface)]"
          >
            Nueva tarea
          </button>
        )}
        <span className="flex shrink-0 gap-1">
          {(['kanban', 'table'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setView(option)}
              className={`rounded px-2.5 py-1 text-[12px] ${
                view === option
                  ? 'bg-[var(--color-surface-muted)] font-medium'
                  : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
              }`}
            >
              {option === 'kanban' ? 'Tablero' : 'Tabla'}
            </button>
          ))}
        </span>
      </div>

      {view === 'kanban' ? (
        <Kanban items={items} onSelect={setSelected} />
      ) : (
        <ItemTable items={items} onSelect={setSelected} />
      )}

      {selected && (
        <ItemPanel
          item={selected}
          me={me.data}
          onClose={() => setSelected(null)}
          onEditar={() => {
            setEditando(selected);
            setSelected(null);
          }}
        />
      )}

      {(creando || editando) && me.data && (
        <PanelLateral
          titulo={creando ? 'Nueva tarea' : 'Editar tarea'}
          onCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
        >
          <TaskEditor
            me={me.data}
            item={editando ?? undefined}
            projectSlug={slug}
            onDone={() => {
              setCreando(false);
              setEditando(null);
            }}
            onCancel={() => {
              setCreando(false);
              setEditando(null);
            }}
          />
        </PanelLateral>
      )}
    </>
  );
}
